import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, Search, Linkedin } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import api from "../../lib/api";
import { PUB_CATEGORIES, IMAGES, FIRM, linkedinEmbed } from "./siteData";
import { useSeo } from "./useSeo";
import PageHero from "./PageHero";

function PubCard({ p, onOpen }) {
  return (
    <button onClick={onOpen} className="site-card-lift text-left bg-white border border-slate-200 p-8 flex flex-col group" data-testid={`pub-card-${p.id}`}>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em]">
        <span className="text-[#C4A66A]">{p.category}</span>
        {p.source === "linkedin"
          ? <span className="inline-flex items-center gap-1.5 text-[#0A66C2] normal-case tracking-normal text-xs font-semibold" data-testid={`pub-linkedin-badge-${p.id}`}><Linkedin className="h-3.5 w-3.5" /> LinkedIn</span>
          : <span className="text-slate-400">{p.read_minutes} min</span>}
      </div>
      <h3 className="font-serif text-2xl font-semibold text-[#14263D] mt-5 leading-snug">{p.title}</h3>
      <p className="text-sm text-slate-600 mt-4 leading-relaxed flex-1">{p.summary}</p>
      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
        <span>{[p.author, p.published_on].filter(Boolean).join(" · ")}</span>
        <ArrowUpRight className="h-4 w-4 text-[#14263D] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </div>
    </button>
  );
}

export default function Publications() {
  useSeo("Let's Learn — Tax Alerts, Audit & Regulatory Insights", "Let's Learn with VCAJ & Associates LLP: tax alerts, audit insights and regulatory updates written for finance teams.");
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [src, setSrc] = useState("all");
  const [params, setParams] = useSearchParams();
  useEffect(() => { api.get("/public/site/publications").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const shown = useMemo(() => (items || []).filter((p) =>
    (cat === "All" || p.category === cat) && (src === "all" || (p.source || "website") === src) &&
    (!q || `${p.title} ${p.summary} ${p.body}`.toLowerCase().includes(q.toLowerCase()))), [items, q, cat, src]);
  const readId = params.get("read");
  const reading = (items || []).find((p) => p.id === readId);
  const setRead = (id) => setParams(id ? { read: id } : {}, { replace: true });

  return (
    <div data-testid="publications-page">
      <PageHero testid="publications-hero" image={IMAGES.learn} eyebrow="Let's Learn" title="Insights, alerts and analysis."
        intro="Short, practical notes on tax, audit and regulatory change — written for the people who have to act on them." />
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-6 lg:items-center justify-between">
            <div className="flex flex-wrap gap-2" data-testid="pub-filters">
              {["All", ...PUB_CATEGORIES].map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`px-4 h-9 text-xs font-semibold tracking-wide border transition-colors ${cat === c ? "bg-[#14263D] text-white border-[#14263D]" : "bg-white border-slate-300 text-slate-600 hover:border-[#14263D]"}`} data-testid={`pub-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</button>
              ))}
            </div>
            <label className="relative w-full lg:w-80">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" className="w-full h-11 pl-11 pr-4 border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#C4A66A]" data-testid="pub-search-input" />
            </label>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="pub-source-filters">
            {[["all", "All insights"], ["website", "Website articles"], ["linkedin", "LinkedIn posts"]].map(([k, l]) => (
              <button key={k} onClick={() => setSrc(k)} className={`px-4 h-9 text-xs font-semibold border-b-2 transition-colors ${src === k ? "border-[#C4A66A] text-[#14263D]" : "border-transparent text-slate-500 hover:text-[#14263D]"}`} data-testid={`pub-source-${k}`}>{l}</button>
            ))}
            <a href={FIRM.linkedin} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-[#0A66C2] hover:underline" data-testid="pub-follow-linkedin"><Linkedin className="h-4 w-4" /> Follow VCAJ on LinkedIn</a>
          </div>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pub-grid">
            {items === null && <div className="text-sm text-slate-500">Loading…</div>}
            {items !== null && shown.length === 0 && <div className="text-slate-500" data-testid="pub-empty">No articles match your search.</div>}
            {shown.map((p) => <PubCard key={p.id} p={p} onOpen={() => setRead(p.id)} />)}
          </div>
        </div>
      </section>
      <Sheet open={!!reading} onOpenChange={(v) => !v && setRead(null)}>
        <SheetContent side="right" className="site w-full sm:max-w-2xl overflow-y-auto" data-testid="pub-reader">
          {reading && (
            <>
              <SheetHeader className="text-left">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#C4A66A]">{reading.category}</div>
                <SheetTitle className="font-serif text-3xl text-[#14263D] leading-tight">{reading.title}</SheetTitle>
                <SheetDescription>{reading.author}{reading.published_on ? ` · ${reading.published_on}` : ""}{reading.source === "linkedin" ? " · LinkedIn post" : ` · ${reading.read_minutes} min read`}</SheetDescription>
              </SheetHeader>
              <div className="site-rule my-8" />
              <p className="text-lg text-slate-700 font-medium leading-relaxed">{reading.summary}</p>
              {reading.body && <div className="mt-6 text-slate-700 leading-relaxed whitespace-pre-line" data-testid="pub-reader-body">{reading.body}</div>}
              {reading.source === "linkedin" && linkedinEmbed(reading.external_url) && (
                <iframe src={linkedinEmbed(reading.external_url)} title={reading.title} className="mt-8 w-full h-[560px] border border-slate-200 bg-white" allowFullScreen data-testid="pub-reader-linkedin-embed" />
              )}
              {reading.external_url && (
                <a href={reading.external_url} target="_blank" rel="noreferrer" className={`mt-8 inline-flex items-center gap-2 text-sm font-semibold site-link ${reading.source === "linkedin" ? "text-[#0A66C2]" : "text-[#14263D]"}`} data-testid="pub-reader-external">
                  {reading.source === "linkedin" ? <><Linkedin className="h-4 w-4" /> View post on LinkedIn</> : <>Read full article <ArrowUpRight className="h-4 w-4" /></>}
                </a>
              )}
              <p className="mt-10 text-xs text-slate-400">For information only — not professional advice.</p>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
